import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import PhoneIcon from '@mui/icons-material/Phone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { buildRoute, ROUTES } from '../../constants/routes';
import Logo from '../common/Logo';
import {
  useGetContactInfoQuery,
  useListPopularTestsQuery,
  useGetMembershipSettingsQuery,
} from '../../store/siteContentApi';

// Mirrors backend ContactInfo defaults — used as a no-flash fallback while
// the API request is in flight.
const CONTACT_DEFAULTS = {
  phone: '74400 75400',
  whatsapp: '+919014638633',
  email: 'support@mytrustlab.com',
  support_hours: '6:00 AM – 10:00 PM daily',
};

// Used until the admin curates the Popular Tests list (or while the API
// request is in flight). Each row routes to the all-tests page since we
// don't have an EDOS id to deep-link to.
const FALLBACK_POPULAR_TESTS = [
  'Complete Blood Count (CBC)',
  'Thyroid Function (TFT)',
  'HbA1c — Diabetes',
  'Vitamin D & B12',
  'Lipid Profile',
  'Liver Function Test',
];

const formatWhatsappForDisplay = (raw) => {
  const digits = String(raw || '').replace(/\D/g, '');
  if (digits.length === 12 && digits.startsWith('91')) {
    const local = digits.slice(2);
    return `${local.slice(0, 5)} ${local.slice(5)}`;
  }
  return raw || '';
};

const FooterLink = ({ to, children, noWrap = false, title }) => (
  <Typography
    component={Link}
    to={to}
    title={title || (noWrap && typeof children === 'string' ? children : undefined)}
    sx={{
      fontSize: 13,
      color: 'rgba(255,255,255,0.55)',
      display: 'block',
      mb: 1,
      textDecoration: 'none',
      transition: 'color 0.2s',
      cursor: 'pointer',
      // When noWrap is on, long names (e.g. "Anti-Microsomal Antibody/
      // Anti-Thyroperoxidase Antibody (Anti-TPO)") get ellipsised at
      // the column's edge instead of wrapping to two lines and
      // visually bleeding into the next column's gap.
      ...(noWrap ? { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } : {}),
      '&:hover': { color: 'var(--sage-l)' },
    }}
  >
    {children}
  </Typography>
);

const ColumnTitle = ({ children }) => (
  <Typography
    component="span"
    sx={{
      display: 'inline-block',
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.2em',
      textTransform: 'uppercase',
      color: 'rgba(255,255,255,0.4)',
      mb: 2,
    }}
  >
    {children}
  </Typography>
);

const ContactRow = ({ Icon, children }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      mb: 1,
      lineHeight: 1,
    }}
  >
    <Icon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', display: 'block', flexShrink: 0 }} />
    <Typography
      component="span"
      sx={{
        fontSize: 13,
        color: 'rgba(255,255,255,0.55)',
        lineHeight: 1,
      }}
    >
      {children}
    </Typography>
  </Box>
);

const Footer = () => {
  const { data: contactFromApi } = useGetContactInfoQuery();
  const contact = contactFromApi || CONTACT_DEFAULTS;

  // Same flag the navbar uses to gate the Membership link — when the admin
  // disables membership, both the nav link AND this footer link hide.
  const { data: membershipSettings } = useGetMembershipSettingsQuery();
  const membershipEnabled = membershipSettings?.is_enabled ?? false;

  // Same admin-curated source as the home-page strip — keeps both in sync.
  // When the list is empty (no admin has curated yet), fall back to a static
  // set of well-known test names that just point at the all-tests page.
  const { data: curatedTests = [] } = useListPopularTestsQuery();
  const footerTests = curatedTests.length > 0
    ? curatedTests.slice(0, 6).map((t) => ({
        name: t.name,
        to: buildRoute(ROUTES.TEST_DETAIL, { id: t.id }),
      }))
    : FALLBACK_POPULAR_TESTS.map((name) => ({ name, to: ROUTES.TESTS }));

  return (
  <Box component="footer" sx={{ bgcolor: '#0A1814', py: { xs: 6, md: 8 } }}>
    <Container maxWidth="xl" sx={{ maxWidth: 1300, px: { xs: 2, md: 4 } }}>
      {/* Equal-width grid was making the empty right-side of the
          Services column LOOK like a bigger gap. Switching to flex
          with `justify-content: space-between`: each column shrinks
          to its content width, then leftover whitespace is split
          evenly between the four columns — so the visible gaps
          between the headers are identical regardless of how short
          each link column's items are. */}
      <Box
        sx={{
          display: { xs: 'grid', md: 'flex' },
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
          },
          columnGap: { xs: 4, md: 5 },
          rowGap: { xs: 4, md: 5 },
          justifyContent: { md: 'space-between' },
          alignItems: { md: 'flex-start' },
          gap: { md: 5 },
          pb: { xs: 4, md: 5 },
          borderBottom: '1px solid rgba(255,255,255,0.07)',
        }}
      >
        {/* Brand column — capped at 320px so the description wraps at
            a readable measure and doesn't expand to fill the row in
            the flex layout. */}
        <Box sx={{ minWidth: 0, flex: { md: '0 1 320px' } }}>
          <Box sx={{ filter: 'brightness(1.5)', mb: 2 }}>
            <Logo dark size="md" />
          </Box>
          <Typography
            sx={{
              fontSize: 13,
              color: 'rgba(255,255,255,0.45)',
              fontWeight: 300,
              lineHeight: 1.7,
              mb: 2.5,
            }}
          >
            NABL-accredited diagnostics with home collection in 60 minutes. Doctor-verified reports in 6 hours,
            delivered to WhatsApp. Serving 13 cities across India.
          </Typography>
          <Box>
            <ContactRow Icon={PhoneIcon}>{contact.phone}</ContactRow>
            <ContactRow Icon={WhatsAppIcon}>WhatsApp: {formatWhatsappForDisplay(contact.whatsapp)}</ContactRow>
            <ContactRow Icon={AccessTimeIcon}>{contact.support_hours}</ContactRow>
            <ContactRow Icon={EmailOutlinedIcon}>{contact.email}</ContactRow>
          </Box>
        </Box>

        {/* Popular Tests column — capped width forces long names to
            ellipsise so this column doesn't outgrow Services / Company
            in the flex row. */}
        <Box sx={{ minWidth: 0, flex: { md: '0 1 220px' } }}>
          <ColumnTitle>Popular Tests</ColumnTitle>
          {footerTests.map((t) => (
            <FooterLink key={`${t.to}-${t.name}`} to={t.to} noWrap>{t.name}</FooterLink>
          ))}
          <Typography
            component={Link}
            to={ROUTES.TESTS}
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--sage-l)',
              display: 'inline-block',
              textDecoration: 'none',
              mt: 0.5,
              '&:hover': { color: '#fff' },
            }}
          >
            All 1700+ tests →
          </Typography>
        </Box>

        {/* Services column */}
        <Box sx={{ minWidth: 0, }}>
          <ColumnTitle>Services</ColumnTitle>
          <FooterLink to={ROUTES.TESTS}>Book a Test</FooterLink>
          <FooterLink to={ROUTES.PACKAGES}>Health Checkups</FooterLink>
          {membershipEnabled && <FooterLink to={ROUTES.MEMBERSHIP}>Membership</FooterLink>}
          <FooterLink to={ROUTES.REPORTS}>My Reports</FooterLink>
          <FooterLink to={ROUTES.CENTRES}>Find a Centre</FooterLink>
          <FooterLink to={ROUTES.CORPORATE}>Corporate Health</FooterLink>
        </Box>

        {/* Company column */}
        <Box sx={{ minWidth: 0 }}>
          <ColumnTitle>Company</ColumnTitle>
          <FooterLink to={ROUTES.ABOUT}>About Us</FooterLink>
          <FooterLink to={`${ROUTES.ABOUT}#leadership`}>Board & Leadership</FooterLink>
          <FooterLink to={ROUTES.DOCTORS}>Our Doctors</FooterLink>
          <FooterLink to={ROUTES.BLOG}>Health Blog</FooterLink>
          <FooterLink to={ROUTES.FAQ}>FAQ</FooterLink>
          <Typography
            component={Link}
            to={ROUTES.SUPPORT}
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--sage-l)',
              display: 'block',
              mb: 1,
              textDecoration: 'none',
              '&:hover': { color: '#fff' },
            }}
          >
            Support Centre
          </Typography>
          <FooterLink to={ROUTES.CAREERS}>Careers</FooterLink>
          <FooterLink to={ROUTES.PRIVACY}>Privacy Policy</FooterLink>
        </Box>
      </Box>

      {/* Bottom strip */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          gap: 2,
          pt: 3,
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            color: 'rgba(255,255,255,0.3)',
            lineHeight: 1.5,
          }}
        >
          © 2026 TrustLab Diagnostics Pvt. Ltd. &nbsp;·&nbsp; Hyderabad &nbsp;·&nbsp; www.mytrustlab.com
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.875,
          }}
        >
          {['NABL', 'NABH', 'ISO 15189', 'ICMR', 'HIPAA'].map((cert) => (
            <Box
              key={cert}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                fontSize: 10,
                fontWeight: 700,
                px: 1.25,
                py: 0.5,
                borderRadius: 100,
                bgcolor: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.45)',
                letterSpacing: '0.08em',
                lineHeight: 1,
              }}
            >
              {cert}
            </Box>
          ))}
        </Box>
      </Box>
    </Container>
  </Box>
  );
};

export default Footer;
